import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useActiveJourney } from "../../contexts/ActiveJourneyContext";
import {
  checkRouteAccess,
  getAccessDeniedContent,
  getRouteAccessKind,
} from "../../utils/routeAccess";
import {
  isAgentHandoffAction,
  type SamiJourneyPayload,
  type SamiJourneyStep,
} from "../../utils/conciergeJourney";
import {
  primaryProductUuid,
  stepTargetPath,
} from "../../utils/journeyStepActions";

interface GuidedJourneyPanelProps {
  journey: SamiJourneyPayload;
  currentStepIndex?: number;
  completedStepIds?: string[];
  onStepAction?: (stepIndex: number) => void;
  variant?: "inline" | "sidebar";
}

const GuidedJourneyPanel: React.FC<GuidedJourneyPanelProps> = ({
  journey,
  currentStepIndex = 0,
  completedStepIds = [],
  onStepAction,
  variant = "inline",
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { token, user } = useAuth();
  const { executeStep } = useActiveJourney();
  const navigate = useNavigate();
  const productUuid = primaryProductUuid(journey);
  const isSidebar = variant === "sidebar";

  const canAccessStep = (step: SamiJourneyStep): boolean => {
    const path = stepTargetPath(step, productUuid);
    const access = getRouteAccessKind(path);
    return checkRouteAccess(access, {
      roles: user?.roles,
      hasToken: Boolean(token),
    });
  };

  const handleStepAction = (stepIndex: number, step: SamiJourneyStep) => {
    if (!canAccessStep(step)) return;
    if (onStepAction) {
      onStepAction(stepIndex);
    } else {
      executeStep(stepIndex);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mt: isSidebar ? 0 : 3,
        textAlign: "left",
      }}
    >
      <CardContent sx={{ py: isSidebar ? 2 : undefined, "&:last-child": { pb: isSidebar ? 2 : undefined } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AutoAwesomeOutlined color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            Your guided journey
          </Typography>
          {journey.confidence && (
            <Chip
              label={`${journey.confidence} confidence`}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {journey.intentSummary}
        </Typography>

        {journey.recommendedProducts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Recommended products
            </Typography>
            {isDesktop ? (
              <Stack spacing={1}>
                {journey.recommendedProducts.map((product) => (
                  <Box
                    key={product.uuid}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={() => navigate(`/product/${product.uuid}`)}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {product.name}
                    </Typography>
                    {product.reason && (
                      <Typography variant="caption" color="text.secondary">
                        {product.reason}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {journey.recommendedProducts.map((product) => (
                  <Chip
                    key={product.uuid}
                    label={product.name}
                    size="small"
                    clickable
                    onClick={() => navigate(`/product/${product.uuid}`)}
                    title={product.reason}
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}

        <Stepper
          orientation="vertical"
          nonLinear
          activeStep={currentStepIndex}
          sx={isSidebar ? { "& .MuiStepContent-root": { ml: 1.5, pl: 2 } } : undefined}
        >
          {journey.steps.map((step, index) => {
            const allowed = canAccessStep(step);
            const denied = !allowed
              ? getAccessDeniedContent(
                  getRouteAccessKind(stepTargetPath(step, productUuid)),
                )
              : null;
            const completed = completedStepIds.includes(step.id);
            return (
              <Step key={step.id} completed={completed} expanded>
                <StepLabel
                  optional={
                    index === journey.steps.length - 1 ? (
                      <Typography variant="caption">Final step</Typography>
                    ) : undefined
                  }
                >
                  {step.title}
                </StepLabel>
                <StepContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    {step.description}
                  </Typography>
                  {!allowed && denied && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ display: "block", mb: 1 }}
                    >
                      {denied.message}
                    </Typography>
                  )}
                  <Button
                    variant={index === currentStepIndex ? "contained" : "outlined"}
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    disabled={!allowed}
                    onClick={() => handleStepAction(index, step)}
                  >
                    {isAgentHandoffAction(step.action)
                      ? "Continue with agent"
                      : "Go to step"}
                  </Button>
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      </CardContent>
    </Card>
  );
};

export default GuidedJourneyPanel;
